/**
 * "리뷰 남겨주세요"를 **언제 물어도 되는가**의 정책 한 곳.
 *
 * 리뷰 창을 띄우는 일 자체는 토스가 하고(lib/toss.ts), 여기서는 그 부탁을 꺼내도 되는
 * 순간만 정한다. 기준은 셋이다:
 *  - 처음 온 사람에게는 묻지 않는다 — 아직 평가할 거리가 없다
 *  - 한 번 넘긴 사람에게는 한참 뒤에 다시 묻는다
 *  - 리뷰 창까지 간 사람에게는 다시 묻지 않는다
 *
 * localStorage에 두는 이유: 회원 설정이 아니라 이 기기에서 몇 번 봤는지일 뿐이라 DB에
 * 올릴 값이 아니고(CLAUDE.md 개인정보 원칙), 탭을 닫아도 살아남아야 한다.
 * 저장소가 막힌 환경에서는 매번 "아직 부탁한 적 없음"으로 읽혀 조금 더 자주 묻는다 —
 * 화면 하나가 더 뜰 뿐이라 그대로 둔다.
 */

const KEY = 'tw:review';

/** 끝까지 본 해석이 이만큼 쌓여야 부탁을 꺼낸다 */
const MIN_READINGS = 2;
/** 부탁을 보여주기만 하고 아무것도 안 눌렀을 때 — 다음 부탁까지 */
const SHOWN_GAP_DAYS = 7;
/** '다음에'를 눌렀을 때 — 다음 부탁까지 */
const SNOOZED_GAP_DAYS = 60;

const DAY_MS = 24 * 60 * 60 * 1000;

interface ReviewState {
  /** 이 기기에서 끝까지 본 해석 수 */
  readings: number;
  /** 이 시각(epoch ms) 전에는 다시 묻지 않는다 */
  nextAskAt?: number;
  /** 리뷰 창까지 갔다 — 영영 묻지 않는다 */
  done?: boolean;
}

function load(): ReviewState {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { readings: 0 };
    const saved = JSON.parse(raw) as Partial<ReviewState>;
    return { ...saved, readings: Number(saved.readings) || 0 };
  } catch {
    return { readings: 0 };
  }
}

function save(state: ReviewState): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* 저장 못 해도 서비스는 그대로 — 다음에 한 번 더 물을 뿐이다 */
  }
}

function defer(days: number, done = false): void {
  const state = load();
  save({ ...state, nextAskAt: Date.now() + days * DAY_MS, ...(done ? { done: true } : {}) });
}

/**
 * 해석을 끝까지 본 순간에 한 번 부른다. 횟수를 올리고 **지금 부탁해도 되는지**를 돌려준다.
 * 세는 일과 판단을 한 번에 하는 이유: 이 리딩까지 포함해서 세야 "두 번째 해석에서 묻는다"가 맞다.
 */
export function noteReadingRevealed(): boolean {
  const state = load();
  const readings = state.readings + 1;
  save({ ...state, readings });
  if (state.done) return false;
  if (readings < MIN_READINGS) return false;
  return !state.nextAskAt || Date.now() >= state.nextAskAt;
}

/** 부탁을 화면에 실제로 띄웠다 — 안 누르고 지나가도 한동안은 다시 묻지 않는다 */
export const markReviewShown = (): void => defer(SHOWN_GAP_DAYS);

/** '다음에' — 거절이므로 훨씬 길게 쉰다 */
export const snoozeReview = (): void => defer(SNOOZED_GAP_DAYS);

/** 리뷰 창까지 갔다 — 다시 묻지 않는다 */
export const markReviewDone = (): void => defer(SNOOZED_GAP_DAYS, true);
